import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import VideoBox from "../../casinoComponents/videoBox";
import BetTableRace20 from "../../casinoComponents/betTableRace20";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatBetNation, formatBetOdds } from "../../../utils/casinoMyBets";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import Race20VideoTitle from "../../casinoComponents/videoBox/overlays/Race20VideoTitle";
import Race20CardsOverlay from "../../casinoComponents/videoBox/overlays/Race20CardsOverlay";
import Race20Rules from "../../casinoComponents/videoBox/overlays/Race20Rules";
import { getRace20RulesHtml } from "../../../data/race20RulesHtml";
import { parseRace20Cards, RACE20_IS_TABLET } from "./race20Cards";
import "../../../styles/games/race20.css";

const RACE20_TITLE = "Race 20";
const rulesHtml = getRace20RulesHtml();

export default function Race20() {
  const [gameData, setGameData] = useState(null);
  const [subData, setSubData] = useState([]);
  const [iframeSrc, setIframeSrc] = useState("");
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "race20";
  // Totals read "Total cards / 90" at their line
  const formatNation = (nat, bet) => formatBetNation(gameName, nat, bet);
  const formatRate = (bet) => formatBetOdds(gameName, bet, bet.odds ?? bet.userrate);

  useEffect(() => {
    setIframeSrc(
      `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`
    );

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
        setSubData(response?.data?.data?.sub || []);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    fetchGameData();
    const gameDataInterval = setInterval(fetchGameData, 1500);
    return () => clearInterval(gameDataInterval);
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId =
        gameData?.data?.data?.t1?.gmid?.toString() ||
        gameData?.data?.data?.mid?.toString() ||
        gameData?.data?.mid?.toString() ||
        gameData?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName =
              item.team_name || item.selection || item.player_name || "";
            const amount = parseFloat(item.exposure_amount) || 0;
            if (teamName) {
              exposureMap[teamName] = amount;
              exposureMap[teamName.toLowerCase()] = amount;
              exposureMap[teamName.trim()] = amount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [
    gameData?.data?.data?.t1?.gmid,
    gameData?.data?.data?.mid,
    gameData?.data?.mid,
    gameData?.mid,
  ]);

  const fetchMyBetsData = useCallback(async () => {
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet:
            bet.matchedBet ||
            bet.nation ||
            bet.selection ||
            bet.player_name ||
            "-",
          selection:
            bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype:
            bet.gametype ??
            bet.game_type ??
            (bet.type === "lay"
              ? "Lay"
              : bet.type === "back"
                ? "Back"
                : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    const matchId =
      gameData?.data?.data?.t1?.gmid?.toString() ||
      gameData?.data?.data?.mid?.toString() ||
      gameData?.data?.mid?.toString() ||
      gameData?.mid?.toString();

    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, gameData]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const resultData =
    gameData?.lrs ??
    gameData?.data?.data?.lrs ??
    gameData?.data?.lrs ??
    [];

  const roundId =
    gameData?.data?.data?.t1?.gmid ||
    gameData?.data?.data?.mid ||
    gameData?.data?.mid ||
    gameData?.mid ||
    "";

  const cardString = gameData?.data?.data?.card || gameData?.data?.data?.t1?.card || "";
  const { totalCards, totalPoints } = parseRace20Cards(cardString);

  const handleRulesClick = () => {
    setShowRules(true);
    // Reference: when IS_MOBILE the rules modal opens too (closing it leaves the panel open)
    if (IS_REF_MOBILE) setShowRulesModal(true);
  };

  // Reference .casino-table.race20: no .game-header — title/round/totals sit inside the video
  return (
    <CasinoTableLayout
      tableClass="race20"
      rightSide={
        <MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} formatNation={formatNation} formatRate={formatRate} />
      }
    >
      <>
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={gameData?.data?.data?.lt}
          roundId={roundId}
          onRulesClick={handleRulesClick}
          titleNode={
            <Race20VideoTitle
              title={RACE20_TITLE}
              roundId={roundId}
              totalCards={totalCards}
              totalPoints={totalPoints}
            />
          }
          cardsOverlay={
            <>
              <Race20CardsOverlay normalizedResultData={cardString} />
              <Race20Rules
                html={rulesHtml}
                show={showRules}
                onClose={() => setShowRules(false)}
                showModal={showRulesModal}
                onCloseModal={() => setShowRulesModal(false)}
              />
            </>
          }
        />
        {roundId ? (
          <div className="casino-detail">
            {IS_REF_MOBILE && RACE20_IS_TABLET && (
              <div className="text-right">
                {`Total Cards: ${totalCards} | Total Points: ${totalPoints}`}
              </div>
            )}
            <BetTableRace20
              onBetClick={handleBetClick}
              data={subData}
              exposures={exposures}
              myBets={myBets}
            />{" "}
            <CasinoLastResult
              gameType="race20"
              data={resultData}
              gameData={gameData}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={formatNation}
        formatRate={formatRate}
      />
    </CasinoTableLayout>
  );
}
